import { useTheme } from "@emotion/react";
import { Add } from "@mui/icons-material";
import {
  Box,
  Fab,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { debounce, isString } from "lodash";
import moment from "moment";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import Iconify from "../../Iconify/Iconify";

/**
 * xlsx ve @react-pdf/renderer birlikte ana bundle'ın büyük kısmını oluşturuyordu.
 * Table her sayfada kullanıldığı için bu iki dışa aktarım aracı ayrı chunk'a alındı.
 */
const ExcelExport = lazy(() => import("../../ExcelExport"));
const PdfDownloader = lazy(() => import("./Pdf"));

const RootStyle = styled(Toolbar)(({ theme }) => ({
  height: 96,
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1, 0, 3),
}));

const SearchStyle = styled(OutlinedInput)(({ theme }: any) => ({
  width: 240,
  transition: theme.transitions.create(["box-shadow", "width"], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  "&.Mui-focused": { width: 320 },
  "& fieldset": {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

export default function TableToolbar(props: any) {
  const theme = useTheme() as any;
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");

  const {
    numSelected,
    onFilter,
    onAdd,
    actions,
    rowActions,
    selecteds,
    title,
    headers,
    dragItem,
    setGroupItems,
    groupItems,
    defaultRowActions,
    groupable,
    disableFilter,
    addText = "add",
    data,
    exportation,
  } = props;

  const handleRemoveGroupItems = (item: any) => {
    const title = headers.find((r: any) => item === r.id)?.id;
    setGroupItems(groupItems.filter((r: any) => r !== title));
  };

  const handleFilter = debounce((e) => {
    onFilter(e);
  }, 400);

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <RootStyle
        sx={{
          ...(numSelected > 0 && {
            color: "primary.main",
            bgcolor: "primary.lighter",
          }),
          height: 60,
          bgcolor: "primary.main",
        }}
      >
        {numSelected > 0 ? (
          <Typography component="div" variant="subtitle1">
            {numSelected} selected
          </Typography>
        ) : (
          <Box sx={{ display: "flex" }}>
            <Typography variant="h5" color="white">
              {title}
            </Typography>
          </Box>
        )}
        {numSelected > 0 ? (
          <div>
            {rowActions &&
              rowActions.map((item: any, index: number) => (
                <Tooltip key={index} title={item.tooltip}>
                  <IconButton onClick={() => item.onPress(selecteds)}>
                    {isString(item.icon) ? (
                      <Iconify icon={item.icon} />
                    ) : (
                      <item.icon />
                    )}
                  </IconButton>
                </Tooltip>
              ))}
          </div>
        ) : (
          <div>
            {exportation && (
              <Suspense fallback={null}>
                <ExcelExport
                  fileName={`${title}-${moment().format("YYYY/MM/DD")}`}
                  data={data}
                  headers={headers}
                />
                {data.length > 0 && (
                  <PdfDownloader data={data} headers={headers} />
                )}
              </Suspense>
            )}
            {!disableFilter && (
              <SearchStyle
                value={filter}
                onChange={(e) => {
                  setFilter(e.target.value);
                  handleFilter(e);
                }}
                placeholder={t("table_search") || ""}
                sx={{ bgcolor: "white", mr: 2, py: 0, height: 40 }}
                startAdornment={
                  <InputAdornment position="start">
                    <Iconify
                      icon="eva:search-fill"
                      sx={{ color: "text.disabled", width: 20, height: 20 }}
                    />
                  </InputAdornment>
                }
              />
            )}
            {defaultRowActions && (
              <Fab
                onClick={onAdd}
                color="inherit"
                variant="extended"
                size="medium"
              >
                {addText ? (
                  <>
                    {t(addText)} <Add sx={{ ml: 1 }} />
                  </>
                ) : (
                  <>
                    <Add />
                  </>
                )}
              </Fab>
            )}
            {actions?.map((item: any, index: number) => (
              <Tooltip key={index} title={item.tooltip}>
                <Box onClick={item.onPress}>
                  {isString(item.icon) ? (
                    <Iconify icon={item.icon} />
                  ) : (
                    <item.icon />
                  )}
                </Box>
              </Tooltip>
            ))}
          </div>
        )}
      </RootStyle>
      {groupable && (
        <Box
          onDrop={(e: any) => {
            console.log(groupItems);
            !groupItems.includes(dragItem) &&
              setGroupItems([...groupItems, dragItem]);
          }}
          onDragOver={(e) => {
            e.preventDefault();
          }}
          sx={{
            height: 44,
            bgcolor: theme.palette.primary.lighter,
            width: "100%",
            display: "flex",
            alignItems: "center",
            py: 1,
            px: 2,
          }}
        >
          {groupItems?.length > 0 ? (
            groupItems?.map((item: any, index: number) => (
              <Box
                key={index}
                component="span"
                onClick={() => handleRemoveGroupItems(item)}
                sx={{
                  cursor: "pointer",
                  bgcolor: "white",
                  color: "primary.main",
                  fontWeight: "bold",
                  height: "100%",
                  display: "flex",
                  fontSize: 14,
                  px: 1.4,
                  py: 1.7,
                  mr: 2,
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #dedede",
                  borderRadius: 1,
                }}
              >
                {headers.find((r: any) => item === r.id)?.label}
                <Box
                  onClick={() => handleRemoveGroupItems(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    paddingLeft: 1,
                    paddingRight: 0,
                  }}
                >
                  <Iconify icon="material-symbols:close" />
                </Box>
              </Box>
            ))
          ) : (
            <Typography variant="body2" color="primary">
              {t("group_drag_field_description")}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}
